// ============================================================
// HOOKED — STRIKE RING  (Phase 3 of the catch loop)
// The bite. A ring spawns at 3× the bobber's radius and closes
// on it. Strike (Space/tap) the instant the ring kisses the
// bobber's circle. PERFECT strikes start the reel with a head
// start; a MISS and the water goes still.
//
// v2 overhaul: the ring is a glowing brass hoop with a trailing
// after-image; the target is a lantern halo; heat sparks kick
// as the ring nears the mark; the fish's silhouette shows in
// the deep. Timing/band math identical.
//
// Modifiers (all decided upstream, priced in Empire resources):
//   ringMs     — the fish's own close speed (FishManifest)
//   slowFactor — MOONWATER (Flow): ×1.4 duration
//   windowMult — TRUE STRIKE (Grit ×2) · Murk marks (×1.12/esc)
//   whipped    — overshot cast: ×0.85 duration
// ============================================================

import { useEffect, useRef, useState } from "react";
import { GM, SD, textGlow } from "./theme";
import { FishArt } from "./FishArt";

const BOBBER_R = 32;      // px — the target circle
const START_MULT = 3;     // ring spawns at 3× bobber radius

// Grade thresholds as |ringR-R|/R — widened by windowMult
const PERFECT_BAND = 0.10;
const GOOD_BAND    = 0.28;
const DEAD_FLOOR   = 0.68; // ring passing below this uncontested = MISS

export function StrikeRing({ ringMs = 1400, windowMult = 1, slowFactor = 1, whipped = false, fishColor = SD.ripple, fish = null, onResolve }) {
  const duration = ringMs * slowFactor * (whipped ? 0.85 : 1);
  const [ringR, setRingR] = useState(BOBBER_R * START_MULT);
  const [hookset, setHookset] = useState(false);   // the jolt on a good set
  const rafRef = useRef(null);
  const t0Ref = useRef(null);
  const doneRef = useRef(false);
  const ringRRef = useRef(BOBBER_R * START_MULT);

  useEffect(() => {
    t0Ref.current = performance.now();
    const tick = (now) => {
      const t = now - t0Ref.current;
      const r = BOBBER_R * (START_MULT - (START_MULT - DEAD_FLOOR) * Math.min(1, t / duration));
      ringRRef.current = r;
      setRingR(r);
      if (t >= duration) {
        if (!doneRef.current) { doneRef.current = true; onResolve?.({ grade: "MISS", late: true }); }
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [duration, onResolve]);

  useEffect(() => {
    const strike = () => {
      if (doneRef.current) return;
      doneRef.current = true;
      cancelAnimationFrame(rafRef.current);
      const delta = Math.abs(ringRRef.current - BOBBER_R) / BOBBER_R;
      let grade = "MISS";
      if (delta <= PERFECT_BAND * windowMult)   grade = "PERFECT";
      else if (delta <= GOOD_BAND * windowMult) grade = "GOOD";
      // a good set JOLTS before the fight begins; a miss just slips
      if (grade === "MISS") {
        onResolve?.({ grade, late: false });
      } else {
        setHookset(true);
        setTimeout(() => onResolve?.({ grade, late: false }), 280);
      }
    };
    const onKey = (e) => {
      if (e.code === "Space" || e.key === "Enter") { e.preventDefault(); strike(); }
    };
    const onTap = () => strike();
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onTap);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onTap);
    };
  }, [windowMult, onResolve]);

  // proximity heat — ring glows hotter as it nears the mark
  const near = Math.max(0, 1 - Math.abs(ringR - BOBBER_R) / (BOBBER_R * 1.2));
  const ringColor = near > 0.85 ? GM.goldBright : near > 0.5 ? SD.foam : fishColor;
  const inPerfect = Math.abs(ringR - BOBBER_R) / BOBBER_R <= PERFECT_BAND * windowMult;

  const box = BOBBER_R * START_MULT * 2 + 40;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 320,
      display: "flex", alignItems: "center", justifyContent: "center",
      pointerEvents: "none",
    }}>
      <div style={{ position: "relative", width: box, height: box }}>
        {/* the fish shadow rising in the deep */}
        {fish && (
          <div style={{
            position: "absolute", left: "50%", top: "50%",
            transform: "translate(-50%,-50%)", opacity: 0.28,
            filter: "blur(1.5px) saturate(0.7)",
          }}>
            <FishArt fish={fish} size={box * 0.62} animate />
          </div>
        )}

        {/* perfect-band aura ring (shows the target window) */}
        <div style={{
          position: "absolute", left: "50%", top: "50%",
          width: BOBBER_R * 2 * (1 + PERFECT_BAND * windowMult), height: BOBBER_R * 2 * (1 + PERFECT_BAND * windowMult),
          transform: "translate(-50%,-50%)", borderRadius: "50%",
          border: `1px dashed ${GM.gold}44`,
          animation: "ringPulseGlow 1.4s ease-in-out infinite",
        }} />

        {/* target circle — the bobber's lantern halo */}
        <div style={{
          position: "absolute", left: "50%", top: "50%",
          width: BOBBER_R * 2, height: BOBBER_R * 2,
          transform: "translate(-50%,-50%)", borderRadius: "50%",
          border: `2px solid ${GM.goldBright}`,
          boxShadow: `0 0 16px ${GM.gold}88, inset 0 0 12px ${GM.gold}44`,
          background: `radial-gradient(circle, ${GM.gold}14, transparent 70%)`,
        }} />
        {/* the bobber itself — JOLTS when the hook sets */}
        <div style={{
          position: "absolute", left: "50%", top: "50%",
          transform: "translate(-50%,-50%)",
          fontSize: 28,
          animation: hookset ? "hooksetJolt 0.28s cubic-bezier(.3,.9,.3,1) forwards" : "bobberBite 0.5s ease forwards",
          filter: `drop-shadow(0 0 8px ${GM.gold}88)`,
        }}>🎈</div>

        {/* hookset flash — the snap of the line going tight */}
        {hookset && (
          <>
            <div style={{
              position: "absolute", left: "50%", top: "50%",
              width: BOBBER_R * 2.4, height: BOBBER_R * 2.4,
              borderRadius: "50%", border: `3px solid ${GM.goldPale}`,
              animation: "hooksetFlash 0.3s ease-out forwards",
            }} />
            {Array.from({ length: 8 }).map((_, k) => (
              <div key={k} style={{
                position: "absolute", left: "50%", top: "50%",
                width: 3, height: 12, borderRadius: 2, background: GM.goldPale,
                transform: `translate(-50%,-50%) rotate(${k * 45}deg) translateY(-${BOBBER_R}px)`,
                boxShadow: `0 0 8px ${GM.goldBright}`,
                animation: "sparkle 0.3s ease-out forwards",
              }} />
            ))}
          </>
        )}
        {/* trailing after-image ring */}
        <div style={{
          position: "absolute", left: "50%", top: "50%",
          width: (ringR + 8) * 2, height: (ringR + 8) * 2,
          transform: "translate(-50%,-50%)", borderRadius: "50%",
          border: `2px solid ${ringColor}`, opacity: 0.25,
        }} />
        {/* the closing ring */}
        <div style={{
          position: "absolute", left: "50%", top: "50%",
          width: ringR * 2, height: ringR * 2,
          transform: "translate(-50%,-50%)", borderRadius: "50%",
          border: `3px solid ${ringColor}`,
          boxShadow: `0 0 ${8 + near * 28}px ${ringColor}, inset 0 0 ${near * 14}px ${ringColor}88`,
          transition: "border-color 0.08s",
        }} />

        {/* heat sparks when near the mark */}
        {near > 0.6 && Array.from({ length: 6 }).map((_, k) => (
          <div key={k} style={{
            position: "absolute", left: "50%", top: "50%",
            width: 3, height: 3, borderRadius: "50%",
            background: inPerfect ? "#fff" : GM.goldBright,
            transform: `translate(-50%,-50%) rotate(${k * 60}deg) translateY(-${BOBBER_R + 4}px)`,
            boxShadow: `0 0 6px ${GM.goldBright}`,
            opacity: near, animation: "sparkle 0.6s ease-out infinite",
          }} />
        ))}

        {/* prompt */}
        <div style={{
          position: "absolute", left: "50%", top: "100%",
          transform: "translateX(-50%)", marginTop: 14,
          fontFamily: GM.fontDisplay, fontSize: 13, letterSpacing: 3,
          color: inPerfect ? "#fff" : GM.goldBright, whiteSpace: "nowrap", ...textGlow(GM.gold),
          transition: "color 0.1s",
        }}>
          ◆ STRIKE ◆
        </div>
      </div>
    </div>
  );
}
